import { HYPERDUSK_DAY_TO_DUSK_CREDIT_COST } from '@rehub/pricing';

export type HyperDuskSceneType = 'exterior' | 'interior';

export const SCENE_TYPE_OPTIONS: { value: HyperDuskSceneType; label: string; description: string }[] = [
  { value: 'exterior', label: 'Exterior', description: 'Outside view of the property' },
  { value: 'interior', label: 'Interior', description: 'Inside a room (living room, kitchen, etc.)' },
];

export type HyperDuskProjectStatus =
  | 'queued'
  | 'generating_clips'
  | 'editing'
  | 'rendering'
  | 'completed'
  | 'failed'
  | 'expired';

export type HyperDuskImageGenerationStatus =
  | 'pending'
  | 'dusk_processing'
  | 'awaiting_user_approval'
  | 'dusk_ready'
  | 'kling_processing'
  | 'animation_ready'
  | 'completed'
  | 'failed';

export interface HyperDuskVariant {
  id: string;
  path: string;
  url?: string | null;
  created_at: string;
  preset_id?: string | null;
  animation_style?: HyperDuskAnimationStyle | null;
  nano_request_id?: string | null;
}

export type HyperDuskAnimationStyle = 'static' | 'gentle_dolly' | 'slow_push_in' | 'cinematic_pan' | 'drone_reveal';

export interface HyperDuskAnimationStyleOption {
  value: HyperDuskAnimationStyle;
  label: string;
  description: string;
}

export const HYPER_DUSK_ANIMATION_STYLES: HyperDuskAnimationStyleOption[] = [
  { value: 'static', label: 'Static', description: 'No camera movement — the shot stays perfectly still while the scene transitions to dusk.' },
  { value: 'gentle_dolly', label: 'Slow Dolly', description: 'Very slow, steady dolly forward toward the property.' },
  { value: 'slow_push_in', label: 'Push In', description: 'Gradual push-in from a wide shot toward the entrance.' },
  { value: 'cinematic_pan', label: 'Cinematic Pan', description: 'Slow horizontal pan across the property facade.' },
  { value: 'drone_reveal', label: 'Drone Reveal', description: 'Camera tilts upward, revealing the building against the sky.' },
];

export const DEFAULT_HYPER_DUSK_ANIMATION_STYLE: HyperDuskAnimationStyle = 'gentle_dolly';

export const DEFAULT_HYPER_DUSK_NANO_PROMPT =
  'Transform this property photo into a dramatic twilight/dusk version for premium real-estate marketing. ' +
  'Replace the daytime sky with a rich blue-hour to early-night gradient sky (deep blues, warm horizon glow). ' +
  'Darken the overall ambient lighting significantly to clearly look like evening. ' +
  'Add warm, realistic interior window glow — every visible window should have inviting golden light coming from inside. ' +
  'Add soft exterior accent lighting on paths, entrance, and landscaping where fixtures would naturally exist. ' +
  'Preserve the architecture, geometry, camera perspective, and every object exactly as the input image. ' +
  'Do not add or remove objects, text, people, cars, or furniture. ' +
  'The result must look unmistakably like nighttime, not just slightly dimmer daylight.';

export const DEFAULT_HYPER_DUSK_NANO_PROMPT_INTERIOR =
  'Transform this interior property photo into a dramatic evening/dusk version for premium real-estate marketing. ' +
  'This is an INTERIOR photo — do NOT paint sky, sunset, or dusk colors onto the ceiling, walls, or any indoor surface. ' +
  'Ceilings must remain their original color and material. Walls must remain their original color. ' +
  'Only modify the view THROUGH windows to show a twilight/dusk sky outside. ' +
  'Darken the overall ambient lighting to clearly look like evening. ' +
  'Add warm, inviting lamp and fixture glow from existing light sources in the room. ' +
  'Make the room feel cozy and warmly lit against the darkening windows. ' +
  'Preserve the interior layout, furniture, decor, geometry, and camera perspective exactly as the input image. ' +
  'Do not add or remove objects, text, people, or furniture. ' +
  'The result must look like a premium evening interior photograph.';

export const DEFAULT_HYPER_DUSK_MOTION_PROMPT =
  'Cinematic real-estate timelapse. The sky gradually shifts from bright daylight to rich twilight as the sun sets. ' +
  'Clouds drift slowly across the sky. Shadows lengthen and deepen across the facade. ' +
  'Interior window lights progressively turn on one by one with warm golden glow. ' +
  'Exterior path lights and accent fixtures illuminate softly. ' +
  'Smooth cinematic dolly forward toward the property with pronounced natural parallax between foreground, facade and sky. ' +
  'Architecture remains structurally fixed — camera travel is the primary motion, not foliage animation. ' +
  'Photorealistic quality throughout. ' +
  'No people, no vehicles, no text, no new objects appear. Architecture and composition remain unchanged.';

export const DEFAULT_HYPER_DUSK_MOTION_PROMPT_INTERIOR =
  'Cinematic real-estate timelapse of an interior room. The view through windows gradually shifts from daylight to twilight. ' +
  'Interior lamps and fixtures gently brighten as the outside darkens. ' +
  'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
  'Smooth dolly forward through the room with natural parallax on furniture and door frames. ' +
  'Architecture and furniture remain structurally fixed. Photorealistic quality throughout. ' +
  'No people, no vehicles, no text, no new objects appear. Interior layout and composition remain unchanged.';

export interface HyperDuskPreset {
  id: string;
  label: string;
  description: string;
  nanoPrompt: string;
  nanoPromptInterior: string;
  klingPromptBase: string;
  klingPromptBaseInterior: string;
  defaultAnimationStyle: HyperDuskAnimationStyle;
}

export const HYPER_DUSK_PRESETS: HyperDuskPreset[] = [
  {
    id: 'cinematic_twilight',
    label: 'Cinematic Twilight',
    description: 'Classic day-to-night timelapse with dramatic sky change and warm interior glow.',
    nanoPrompt: DEFAULT_HYPER_DUSK_NANO_PROMPT,
    nanoPromptInterior: DEFAULT_HYPER_DUSK_NANO_PROMPT_INTERIOR,
    klingPromptBase: DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    klingPromptBaseInterior: DEFAULT_HYPER_DUSK_MOTION_PROMPT_INTERIOR,
    defaultAnimationStyle: 'gentle_dolly',
  },
  {
    id: 'twilight_lights_on',
    label: 'Twilight Lights On',
    description: 'Bright interior and exterior lights illuminate the property at dusk.',
    nanoPrompt:
      'Transform this property photo into a dramatic night scene with all architectural lighting switched on. ' +
      'Every visible window must glow with warm, inviting interior light. Activate exterior lighting fixtures, path lights, and landscape uplighting. ' +
      'Replace the sky with a deep twilight gradient (dark blue to warm orange horizon). Darken ambient lighting to clearly read as evening. ' +
      'Preserve architecture, perspective, and all objects exactly as-is. ' +
      'Do not add or remove people, cars, furniture, text, or any new elements. ' +
      'The result must look unmistakably like nighttime with the property beautifully lit up.',
    nanoPromptInterior:
      'Transform this interior property photo into a warm evening scene with all room lighting switched on. ' +
      'This is an INTERIOR photo — do NOT paint sky, sunset, or dusk colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show a deep twilight sky outside. ' +
      'Turn on all visible lamps, pendants, sconces, and recessed lighting with warm golden glow. ' +
      'Darken ambient lighting to clearly read as evening. ' +
      'Preserve the interior layout, furniture, decor, and all objects exactly as-is. ' +
      'Do not add or remove people, furniture, text, or any new elements.',
    klingPromptBase:
      'Cinematic real-estate timelapse. The sky transitions from daylight to deep twilight. ' +
      'Building lights come alive one by one — warm golden glow fills every window progressively. ' +
      'Exterior path lights, porch lights, and landscape uplighting switch on sequentially. ' +
      'Shadows deepen across the facade as ambient light fades. ' +
      'Photorealistic quality. No people, no vehicles, no text. Architecture unchanged.',
    klingPromptBaseInterior:
      'Cinematic real-estate timelapse of an interior room. The view through windows transitions from daylight to deep twilight. ' +
      'Room lights come alive one by one — lamps, pendants, and fixtures glow warmly. ' +
      'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
      'Photorealistic quality. No people, no vehicles, no text. Interior layout unchanged.',
    defaultAnimationStyle: 'slow_push_in',
  },
  {
    id: 'moody_blue_hour',
    label: 'Moody Blue Hour',
    description: 'Deep blue dramatic night sky with cool shadows and crisp contrast.',
    nanoPrompt:
      'Transform this property listing photo into a dramatic late blue-hour night scene. ' +
      'Replace the sky with a deep, dark blue evening sky with subtle cloud detail. ' +
      'Darken the overall scene significantly — the building should be silhouetted against the sky. ' +
      'Add warm interior window glow as contrast against the dark exterior. ' +
      'Preserve architecture, geometry, and all objects exactly as captured. ' +
      'Do not add people, cars, furniture, text, or synthetic elements. ' +
      'The result must look clearly like late evening, not just a colour filter.',
    nanoPromptInterior:
      'Transform this interior property photo into a moody blue-hour evening scene. ' +
      'This is an INTERIOR photo — do NOT paint sky, sunset, or blue-hour colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show a deep, dark blue evening sky. ' +
      'Darken the room significantly. Add warm lamp glow as contrast against the cool blue light from windows. ' +
      'Preserve the interior layout, furniture, decor, and all objects exactly as captured. ' +
      'Do not add people, furniture, text, or synthetic elements.',
    klingPromptBase:
      'Cinematic real-estate timelapse. The sky deepens from daylight into a moody, dark blue-hour atmosphere. ' +
      'Clouds drift slowly through the deep blue sky. Cool shadows creep across the building. ' +
      'Warm interior window glow appears as contrast against the darkening exterior. ' +
      'Dramatic, contrasty lighting. Photorealistic quality. ' +
      'No people, no vehicles, no text. Architecture unchanged.',
    klingPromptBaseInterior:
      'Cinematic real-estate timelapse of an interior room. Cool blue-hour light filters through windows as the outside darkens. ' +
      'Warm room lamps contrast against the cool window light. ' +
      'Do NOT apply sky or blue-hour colors to the ceiling, walls, or any indoor surface. ' +
      'Dramatic, contrasty lighting. Photorealistic quality. ' +
      'No people, no vehicles, no text. Interior layout unchanged.',
    defaultAnimationStyle: 'cinematic_pan',
  },
  {
    id: 'warm_luxury_glow',
    label: 'Warm Luxury Glow',
    description: 'Rich golden-hour evening with premium warm lighting throughout.',
    nanoPrompt:
      'Transform this property photo into a luxurious warm evening scene. ' +
      'Replace the sky with a rich sunset-to-dusk gradient (golden, amber, deep purple). ' +
      'Darken ambient lighting to clearly read as evening. ' +
      'Add warm, rich window glow in every visible window. Add soft accent lighting on architectural features. ' +
      'Keep the exact scene composition and all visible objects unchanged. ' +
      'Do not add people, text, or any new elements. ' +
      'The result must look like a premium twilight real-estate photograph.',
    nanoPromptInterior:
      'Transform this interior property photo into a luxurious warm evening scene. ' +
      'This is an INTERIOR photo — do NOT paint sky, sunset, or golden colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show a warm sunset-to-dusk gradient outside. ' +
      'Darken ambient lighting to clearly read as evening. Add warm, rich glow from lamps and fixtures. ' +
      'Keep the exact interior composition and all visible objects unchanged. ' +
      'Do not add people, text, or any new elements.',
    klingPromptBase:
      'Premium real-estate timelapse. Golden sunset light gradually warms the building facade. ' +
      'The sky shifts from bright daylight through rich amber and golden tones into deep purple dusk. ' +
      'Warm, luxurious window glow intensifies in every window. Accent lighting highlights architectural details. ' +
      'Slow, elegant camera movement. Photorealistic quality. ' +
      'No people, no vehicles, no text. Architecture unchanged.',
    klingPromptBaseInterior:
      'Premium real-estate timelapse of an interior room. Warm golden light from windows gradually shifts as the sun sets outside. ' +
      'Interior lamps and fixtures intensify with warm, luxurious glow. ' +
      'Do NOT apply sky or sunset colors to the ceiling, walls, or any indoor surface. ' +
      'Slow, elegant camera movement. Photorealistic quality. ' +
      'No people, no vehicles, no text. Interior layout unchanged.',
    defaultAnimationStyle: 'slow_push_in',
  },
  {
    id: 'dramatic_sunset',
    label: 'Dramatic Sunset',
    description: 'Vivid orange and pink sunset sky with rich golden light on the property.',
    nanoPrompt:
      'Transform this property photo into a dramatic sunset scene for premium real-estate marketing. ' +
      'Replace the sky with a vivid, photorealistic sunset — rich oranges, deep pinks, and warm golden tones radiating from the horizon. ' +
      'Cast warm golden-hour light across the building facade and surrounding landscape. ' +
      'Add warm interior window glow where windows are visible. ' +
      'Preserve the architecture, geometry, camera perspective, and every object exactly as the input image. ' +
      'Do not add or remove objects, text, people, cars, or furniture. ' +
      'The result must look like a stunning golden-hour real-estate photograph with a vivid sky.',
    nanoPromptInterior:
      'Transform this interior property photo into a dramatic sunset-lit evening scene. ' +
      'This is an INTERIOR photo — do NOT paint sunset, orange, or pink colors onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show a vivid sunset — rich oranges, pinks, and golden tones. ' +
      'Cast subtle warm golden-hour light entering through the windows onto nearby surfaces only. ' +
      'Preserve the interior layout, furniture, decor, and every object exactly as the input image. ' +
      'Do not add or remove objects, text, people, or furniture.',
    klingPromptBase:
      'Cinematic real-estate timelapse. A vivid, dramatic sunset unfolds across the sky — rich oranges, deep pinks, and golden tones radiate from the horizon. ' +
      'Golden-hour light washes across the building facade, casting long warm shadows. ' +
      'Clouds catch fire with sunset colors, drifting slowly. Interior windows begin to glow warmly. ' +
      'Photorealistic quality. No people, no vehicles, no text. Architecture unchanged.',
    klingPromptBaseInterior:
      'Cinematic real-estate timelapse of an interior room. A vivid sunset unfolds in the view through windows. ' +
      'Warm golden light streams through windows, casting soft shadows across the floor. ' +
      'Do NOT apply sunset colors to the ceiling, walls, or any indoor surface. ' +
      'Interior lamps begin to glow warmly. Photorealistic quality. ' +
      'No people, no vehicles, no text. Interior layout unchanged.',
    defaultAnimationStyle: 'cinematic_pan',
  },
  {
    id: 'stormy_cloudscape',
    label: 'Stormy Cloudscape',
    description: 'Dramatic moody clouds with brooding atmosphere and contrast lighting.',
    nanoPrompt:
      'Transform this property photo with a dramatic, moody cloudscape sky for cinematic real-estate marketing. ' +
      'Replace the sky with dark, textured storm clouds — layered greys, deep charcoals, with occasional breaks of warm light on the horizon. ' +
      'Keep the property well-lit with warm interior window glow to contrast against the dramatic sky. ' +
      'Add subtle wet-ground reflections or damp atmosphere if pavement or paths are visible. ' +
      'Preserve the architecture, geometry, camera perspective, and every object exactly as the input image. ' +
      'Do not add or remove objects, text, people, cars, or furniture. ' +
      'The result must look like a premium architectural photograph with dramatic weather.',
    nanoPromptInterior:
      'Transform this interior property photo with a dramatic stormy atmosphere visible through windows. ' +
      'This is an INTERIOR photo — do NOT paint storm clouds, grey tones, or weather effects onto the ceiling, walls, or any indoor surface. ' +
      'Ceilings and walls must remain their original color and material. ' +
      'Only modify the view THROUGH windows to show dark, textured storm clouds. ' +
      'Keep the room well-lit with warm lamp glow to contrast against the moody windows. ' +
      'Preserve the interior layout, furniture, decor, and every object exactly as the input image. ' +
      'Do not add or remove objects, text, people, or furniture.',
    klingPromptBase:
      'Cinematic real-estate timelapse. Dark storm clouds roll in dramatically across the sky, layered greys and charcoals churning with motion. ' +
      'Breaks of warm light pierce through the cloud cover onto the property. ' +
      'Wind subtly moves trees and foliage. Moody, brooding atmosphere intensifies. ' +
      'Warm interior window glow contrasts against the darkening storm. ' +
      'Photorealistic quality. No people, no vehicles, no text. Architecture unchanged.',
    klingPromptBaseInterior:
      'Cinematic real-estate timelapse of an interior room. Storm clouds darken the view through windows. ' +
      'Warm room lighting contrasts against the moody, dark window views. ' +
      'Do NOT apply storm or cloud effects to the ceiling, walls, or any indoor surface. ' +
      'Photorealistic quality. No people, no vehicles, no text. Interior layout unchanged.',
    defaultAnimationStyle: 'slow_push_in',
  },
];

export const DEFAULT_HYPER_DUSK_PRESET_ID = HYPER_DUSK_PRESETS[0].id;

export type HyperDuskOutputMode = 'image' | 'video';

export const HYPER_DUSK_VIDEO_CREDITS = 7;
export const HYPER_DUSK_IMAGE_CREDITS = HYPERDUSK_DAY_TO_DUSK_CREDIT_COST;
export const HYPER_DUSK_MAX_IMAGE_VARIANTS = 3;
// Top-up to upgrade a finished image-only project into a full video —
// the difference between the two products so the total matches buying
// the video outright (image + top-up = video, e.g. 1 + 6 = 7).
export const HYPER_DUSK_IMAGE_TO_VIDEO_CREDITS = HYPER_DUSK_VIDEO_CREDITS - HYPER_DUSK_IMAGE_CREDITS;
// Backwards-compatible alias — the full video product cost.
export const HYPER_DUSK_CREDITS = HYPER_DUSK_VIDEO_CREDITS;

export interface HyperDuskProject {
  id: string;
  user_id: string;
  name: string | null;
  status: HyperDuskProjectStatus;
  service_type?: 'hyper_dusk' | 'standard';
  settings?: {
    output_mode?: HyperDuskOutputMode;
    aspect_ratio?: string;
    clip_duration?: number;
    [key: string]: unknown;
  } | null;
  error_message: string | null;
  final_video_url: string | null;
  final_video_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface HyperDuskProjectImage {
  id: string;
  project_id: string;
  image_url: string;
  image_path: string | null;
  order_index: number;
  clip_url: string | null;
  clip_duration: number;
  source_image_url?: string | null;
  source_image_path?: string | null;
  dusk_image_url?: string | null;
  dusk_image_path?: string | null;
  dusk_variants?: HyperDuskVariant[] | null;
  selected_dusk_variant_id?: string | null;
  kling_animation_style?: HyperDuskAnimationStyle | null;
  generation_status?: HyperDuskImageGenerationStatus | null;
  generation_error?: string | null;
  created_at: string;
}
